import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  Animated,
  Easing,
} from "react-native";
import { useTranslation } from "react-i18next";
import { BlurView } from "expo-blur";

import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import GlobalStyles from "../../../Resources/Styles";
import { useDevice } from "../../_layout";

const AnimatedBlurView = Animated.createAnimatedComponent(BlurView);

const BlockViewUpdateModal = ({
  isVisible,
  onTryItOut,
  titleKey,
  bulletKeys,
  buttonKey,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useDevice();
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isContentReady, setIsContentReady] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const overlayAnimation = useRef(new Animated.Value(0)).current;
  const modalAnimation = useRef(new Animated.Value(0)).current;
  const buttonAnimation = useRef(new Animated.Value(0)).current;
  const introAnimation = useRef(new Animated.Value(0)).current;
  const bulletAnimations = useMemo(
    () => bulletKeys.map(() => new Animated.Value(0)),
    [bulletKeys]
  );
  const animationRef = useRef(null);

  useEffect(() => {
    if (!isVisible) {
      setIsContentReady(false);
      setIsClosing(false);
      overlayAnimation.setValue(0);
      modalAnimation.setValue(0);
      buttonAnimation.setValue(0);
      introAnimation.setValue(0);
      bulletAnimations.forEach((animation) => animation.setValue(0));
      if (animationRef.current) {
        animationRef.current.stop();
      }
      return;
    }

    setIsContentReady(false);
    setIsClosing(false);
    overlayAnimation.setValue(0);
    modalAnimation.setValue(0);
    buttonAnimation.setValue(0);
    introAnimation.setValue(0);
    bulletAnimations.forEach((animation) => animation.setValue(0));

    const overlayTiming = Animated.timing(overlayAnimation, {
      toValue: 1,
      duration: 300,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    });

    const modalTiming = Animated.timing(modalAnimation, {
      toValue: 1,
      duration: 400,
      easing: Easing.out(Easing.back(1.1)),
      useNativeDriver: true,
    });

    const introTiming = Animated.timing(introAnimation, {
      toValue: 1,
      duration: 400,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    });

    const buttonTiming = Animated.timing(buttonAnimation, {
      toValue: 1,
      duration: 400,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    });

    const bulletTimings = bulletAnimations.map((animation) =>
      Animated.timing(animation, {
        toValue: 1,
        duration: 400,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      })
    );

    const bulletSequence =
      bulletTimings.length > 0
        ? Animated.stagger(100, bulletTimings)
        : Animated.delay(0);

    animationRef.current = Animated.sequence([
      overlayTiming,
      Animated.delay(50),
      modalTiming,
      Animated.delay(100),
      introTiming,
      Animated.delay(100),
      bulletSequence,
      Animated.delay(150),
      buttonTiming,
    ]);

    animationRef.current.start(({ finished }) => {
      if (finished) {
        setIsContentReady(true);
      }
    });

    return () => {
      if (animationRef.current) {
        animationRef.current.stop();
      }
    };
  }, [
    bulletAnimations,
    buttonAnimation,
    introAnimation,
    isVisible,
    modalAnimation,
    overlayAnimation,
  ]);

  const getAnimatedStyle = (animation, translateOffset = 6) => ({
    opacity: animation,
    transform: [
      {
        translateY: animation.interpolate({
          inputRange: [0, 1],
          outputRange: [translateOffset, 0],
        }),
      },
    ],
  });
  const modalAnimatedStyle = {
    opacity: modalAnimation,
    transform: [
      {
        translateY: modalAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: [-28, 0],
        }),
      },
      {
        scale: modalAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: [0.98, 1],
        }),
      },
    ],
  };
  const overlayAnimatedStyle = {
    opacity: overlayAnimation,
  };
  const buttonAnimatedStyle = {
    opacity: buttonAnimation,
    transform: [
      {
        translateY: buttonAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: [8, 0],
        }),
      },
      {
        scale: buttonAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: [0.98, 1],
        }),
      },
    ],
  };

  const handleTryItOut = () => {
    if (isClosing || !isContentReady) return;
    setIsClosing(true);
    if (animationRef.current) {
      animationRef.current.stop();
    }

    Animated.parallel([
      Animated.timing(buttonAnimation, {
        toValue: 0,
        duration: 180,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(modalAnimation, {
        toValue: 0,
        duration: 260,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(overlayAnimation, {
        toValue: 0,
        duration: 260,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start(({ finished }) => {
      if (finished) {
        onTryItOut?.();
      }
    });
  };

  return (
    <Modal animationType="fade" transparent={true} visible={isVisible}>
      <View style={GlobalStyles.centeredView}>
        <Animated.View style={[styles.blurBackground, overlayAnimatedStyle]}>
          <AnimatedBlurView
            intensity={18}
            tint="dark"
            style={styles.blurLayer}
          />
        </Animated.View>
        <Animated.View style={[styles.backdrop, overlayAnimatedStyle]} />
        <Animated.View style={[styles.modalView, modalAnimatedStyle]}>
          <View style={styles.headerRow}>
            <View style={styles.badge}>
              <Text style={styles.badgeText}>
                {t("blockViewUpdateBadge-string")}
              </Text>
            </View>
          </View>
          <Text style={[Fonts.bold18, styles.title]}>{t(titleKey)}</Text>
          <Animated.View style={getAnimatedStyle(introAnimation, 8)}>
            <Text style={styles.introText}>
              {t("blockViewUpdateIntro-string")}
            </Text>
          </Animated.View>
          <View style={styles.bulletList}>
            {bulletKeys.map((key, index) => (
              <Animated.View
                key={key}
                style={[
                  styles.bulletRow,
                  getAnimatedStyle(bulletAnimations[index]),
                ]}
              >
                <View style={styles.bulletDot} />
                <Text style={styles.bulletText}>{t(key)}</Text>
              </Animated.View>
            ))}
          </View>
          <View style={styles.actionsRow}>
            <Animated.View style={buttonAnimatedStyle}>
              <TouchableOpacity
                style={[
                  styles.primaryButton,
                  isButtonHovered && styles.primaryButtonHovered,
                ]}
                disabled={!isContentReady || isClosing}
                pointerEvents={isContentReady && !isClosing ? "auto" : "none"}
                onPress={handleTryItOut}
                onMouseEnter={() => !isMobile && setIsButtonHovered(true)}
                onMouseLeave={() => setIsButtonHovered(false)}
              >
                <Text style={styles.primaryText}>{t(buttonKey)}</Text>
              </TouchableOpacity>
            </Animated.View>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  blurBackground: {
    ...StyleSheet.absoluteFillObject,
  },
  blurLayer: {
    ...StyleSheet.absoluteFillObject,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(15, 15, 15, 0.2)",
  },
  modalView: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 18,
    padding: 26,
    width: "auto",
    maxWidth: "90%",
    alignSelf: "center",
    gap: 12,
    zIndex: 1000,
    elevation: 10,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.22,
    shadowRadius: 6,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  badge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: Colors.bubbleColor,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: "600",
    color: Colors.whiteColor,
    letterSpacing: 0.6,
    textTransform: "uppercase",
  },
  title: {
    fontSize: 18,
  },
  introText: {
    fontSize: 14,
    color: Colors.darkgrayColor,
    lineHeight: 18,
  },
  bulletList: {
    gap: 10,
  },
  bulletRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  bulletDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: Colors.bubbleColor,
    marginTop: 6,
  },
  bulletText: {
    flex: 1,
    fontSize: 14,
    color: Colors.darkgrayColor,
    lineHeight: 18,
  },
  actionsRow: {
    marginTop: 8,
    flexDirection: "row",
    justifyContent: "flex-end",
    minHeight: 36,
  },
  primaryButton: {
    height: 36,
    paddingHorizontal: 18,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.bubbleColor,
    backgroundColor: Colors.bubbleColor,
    alignItems: "center",
    justifyContent: "center",
  },
  primaryButtonHovered: {
    opacity: 0.9,
  },
  primaryText: {
    fontSize: 14,
    fontWeight: "600",
    color: Colors.whiteColor,
  },
});

export default BlockViewUpdateModal;
